import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import styles from "./UpdateSubscriptionPlanForm.module.css";
import Button from "../../UI/Button/Button";
import FeedbackModal from "../../UI/FeedbackModal/FeedbackModal";
import {
  getSubscriptionPlans,
  getSubscriptionPlanById,
  updateSubscriptionPlan,
  uploadSubscriptionPlanImage,
} from "../../../services/subscription-plan-service";

const websiteTypeOptions = [
  { key: "ecommerce", value: "E-commerce / Online Store" },
  { key: "marketplace", value: "Marketplace" },
  { key: "business", value: "Business / Corporate Website" },
  { key: "news", value: "News & Magazine" },
  { key: "blog", value: "Blog" },
  { key: "videoStreaming", value: "Video Streaming" },
  { key: "portfolio", value: "Portfolio Site" },
  { key: "socialNetworks", value: "Social Networks" },
  { key: "communityForums", value: "Community Forums" },
  { key: "elearning", value: "E-learning Platform" },
  { key: "healthFitness", value: "Health & Fitness" },
  {
    key: "medicalService",
    value: "Doctor Appointment / Medical Service Sites",
  },
  { key: "travelBooking", value: "Travel & Booking" },
  { key: "foodDelivery", value: "Food Delivery Platforms" },
  { key: "jobBoards", value: "Job Boards & Career Sites" },
  { key: "banking", value: "Banking & Financial Institutions" },
  { key: "investment", value: "Investment Platforms" },
  { key: "nonprofit", value: "Nonprofit / Charity Websites" },
  { key: "appLanding", value: "App Landing Pages" },
  { key: "saasProduct", value: "SaaS Product Websites" },
];

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function UpdateSubscriptionPlanForm() {
  const { t } = useTranslation("common");
  const [plans, setPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const projectImageInputRef = useRef(null);
  const [projectImagePreview, setProjectImagePreview] = useState("");
  const [projectImageFile, setProjectImageFile] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState("info");
  const [modalTitle, setModalTitle] = useState("");
  const [modalMessage, setModalMessage] = useState("");

  const [formData, setFormData] = useState({
    clientEmail: "",
    projectType: "Web Application",
    projectTier: "Basic",
    subscriptionPlan: "Basic",
    projectId: "",
    websiteType: websiteTypeOptions[0].value,
    product: "",
    amount: "",
    currency: "RON",
    renewalDate: todayISO(),
    status: "active",
    autoRenew: true,
    projectImage: "",
    scope: true,
    projectUrl: "",
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setIsLoading(true);
    try {
      const result = await getSubscriptionPlans();
      if (result.success) {
        setPlans(result.data || []);
      } else {
        setModalType("error");
        setModalTitle("Error");
        setModalMessage(result.error || "Failed to load subscription plans");
        setModalOpen(true);
      }
    } catch (error) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage("Failed to load subscription plans");
      setModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePlanSelect = async (e) => {
    const planId = e.target.value;
    if (!planId) {
      setSelectedPlan(null);
      return;
    }

    setIsLoading(true);
    try {
      const result = await getSubscriptionPlanById(planId);
      if (result.success) {
        const plan = result.data;
        setSelectedPlan(plan);

        let renewalDate = todayISO();
        if (plan.renewalDate) {
          const date = new Date(plan.renewalDate);
          renewalDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
        }

        setFormData({
          clientEmail: plan.clientEmail || "",
          projectType: plan.projectType || "Web Application",
          projectTier: plan.projectTier || "Basic",
          subscriptionPlan:
            plan.subscriptionPlan || plan.projectTier || "Basic",
          projectId: plan.projectId || "",
          websiteType: plan.websiteType || websiteTypeOptions[0].value,
          product: plan.product || "",
          amount: plan.amount?.toString() || "",
          currency: plan.currency || "RON",
          renewalDate: renewalDate,
          status: plan.status || "active",
          autoRenew: plan.autoRenew !== false,
          projectImage: plan.projectImage || "",
          scope: plan.scope !== false,
          projectUrl: plan.projectUrl || "",
        });

        setProjectImagePreview(plan.projectImage || "");
        setProjectImageFile(null);
      } else {
        throw new Error(result.error || "Failed to load subscription plan");
      }
    } catch (error) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage(error.message || "Failed to load subscription plan");
      setModalOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({
      ...p,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleProjectImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/gif",
    ];
    if (!allowedTypes.includes(file.type)) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage(
        "Please select a valid image file (JPEG, PNG, WebP, or GIF)",
      );
      setModalOpen(true);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage("File size must be less than 10MB");
      setModalOpen(true);
      return;
    }

    setProjectImageFile(file);
    const previewUrl = URL.createObjectURL(file);
    setProjectImagePreview(previewUrl);

    if (projectImageInputRef.current) {
      projectImageInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPlan) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage("Please select a subscription plan to update");
      setModalOpen(true);
      return;
    }

    setIsSubmitting(true);

    try {
      let projectImageUrl = formData.projectImage;

      if (projectImageFile) {
        const websiteName =
          formData.product?.trim() ||
          formData.projectUrl?.trim() ||
          "subscription-project";
        const result = await uploadSubscriptionPlanImage(
          projectImageFile,
          websiteName,
          "main",
        );
        if (result.success && result.data?.imageUrl) {
          projectImageUrl = result.data.imageUrl;
        } else {
          throw new Error(
            result.message || "Failed to upload subscription plan image",
          );
        }
      }

      const payload = { ...formData, projectImage: projectImageUrl };
      const result = await updateSubscriptionPlan(selectedPlan._id, payload);

      if (result.success) {
        setModalType("success");
        setModalTitle("Success");
        setModalMessage("Subscription plan updated successfully!");
        setModalOpen(true);
        await loadPlans();
      } else {
        throw new Error(result.error || "Failed to update subscription plan");
      }
    } catch (error) {
      setModalType("error");
      setModalTitle("Error");
      setModalMessage(error.message || "Failed to update subscription plan");
      setModalOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className={styles.projectForm}>
        <div className={styles.formGroup}>
          <label htmlFor="planSelect" className={styles.label}>
            Select Subscription Plan to Update *
          </label>
          <select
            id="planSelect"
            name="planSelect"
            value={selectedPlan?._id || ""}
            onChange={handlePlanSelect}
            className={styles.select}
            required
            disabled={isLoading || isSubmitting}
          >
            <option value="">
              {isLoading ? "Loading plans..." : "Select a subscription plan..."}
            </option>
            {plans.map((plan) => (
              <option key={plan._id} value={plan._id}>
                {plan.product || plan.projectId || plan._id} ({plan.clientEmail}
                )
              </option>
            ))}
          </select>
        </div>

        {selectedPlan && (
          <>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="clientEmail" className={styles.label}>
                  {t("forms.portalSubscription.labels.clientEmail")} *
                </label>
                <input
                  type="email"
                  id="clientEmail"
                  name="clientEmail"
                  value={formData.clientEmail}
                  onChange={handleInputChange}
                  className={styles.input}
                  placeholder={t(
                    "forms.portalSubscription.placeholders.clientEmailExample",
                  )}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="projectType" className={styles.label}>
                  {t("forms.portalSubscription.labels.projectType")} *
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleInputChange}
                  className={styles.select}
                  required
                  disabled={isSubmitting}
                >
                  <option value="Web Application">
                    {t("forms.portalSubscription.options.webApplication")}
                  </option>
                  <option value="Mobile Application">
                    {t("forms.portalSubscription.options.mobileApplication")}
                  </option>
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="projectTier" className={styles.label}>
                  {t("forms.portalSubscription.labels.projectTier")} *
                </label>
                <select
                  id="projectTier"
                  name="projectTier"
                  value={formData.projectTier}
                  onChange={handleInputChange}
                  className={styles.select}
                  required
                  disabled={isSubmitting}
                >
                  <option value="Basic">
                    {t("forms.portalSubscription.options.basic")}
                  </option>
                  <option value="Premium">
                    {t("forms.portalSubscription.options.premium")}
                  </option>
                  <option value="Enterprise">
                    {t("forms.portalSubscription.options.enterprise")}
                  </option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="projectId" className={styles.label}>
                  {t("forms.portalSubscription.labels.projectId")}
                </label>
                <input
                  type="text"
                  id="projectId"
                  name="projectId"
                  value={formData.projectId}
                  onChange={handleInputChange}
                  className={styles.input}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="websiteType" className={styles.label}>
                  {t("forms.portalSubscription.labels.websiteType")} *
                </label>
                <select
                  id="websiteType"
                  name="websiteType"
                  value={formData.websiteType}
                  onChange={handleInputChange}
                  className={styles.select}
                  required
                  disabled={isSubmitting}
                >
                  {websiteTypeOptions.map((opt) => (
                    <option key={opt.key} value={opt.value}>
                      {t(`forms.clientProject.options.websiteTypes.${opt.key}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="product" className={styles.label}>
                  {t("forms.portalSubscription.labels.product")} *
                </label>
                <input
                  type="text"
                  id="product"
                  name="product"
                  value={formData.product}
                  onChange={handleInputChange}
                  className={styles.input}
                  placeholder={t(
                    "forms.portalSubscription.placeholders.enterProductName",
                  )}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="amount" className={styles.label}>
                  {t("forms.portalSubscription.labels.amount")} *
                </label>
                <input
                  type="number"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  className={styles.input}
                  placeholder={t(
                    "forms.portalSubscription.placeholders.amountExample",
                  )}
                  min="0"
                  step="0.01"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="currency" className={styles.label}>
                  {t("forms.portalSubscription.labels.currency")}
                </label>
                <select
                  id="currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  className={styles.select}
                  disabled={isSubmitting}
                >
                  <option value="EUR">
                    {t("forms.portalSubscription.options.eur")}
                  </option>
                  <option value="USD">
                    {t("forms.portalSubscription.options.usd")}
                  </option>
                  <option value="GBP">
                    {t("forms.portalSubscription.options.gbp")}
                  </option>
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="renewalDate" className={styles.label}>
                  {t("forms.portalSubscription.labels.renewalDate")}
                </label>
                <input
                  type="date"
                  id="renewalDate"
                  name="renewalDate"
                  value={formData.renewalDate}
                  onChange={handleInputChange}
                  className={styles.input}
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="status" className={styles.label}>
                  {t("forms.portalSubscription.labels.status")}
                </label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className={styles.select}
                  disabled={isSubmitting}
                >
                  <option value="inactive">
                    {t("forms.portalSubscription.options.inactive")}
                  </option>
                  <option value="active">
                    {t("forms.portalSubscription.options.active")}
                  </option>
                  <option value="failed">
                    {t("forms.portalSubscription.options.failed")}
                  </option>
                  <option value="expired">
                    {t("forms.portalSubscription.options.expired")}
                  </option>
                  <option value="canceled">
                    {t("forms.portalSubscription.options.canceled")}
                  </option>
                </select>
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="projectUrl" className={styles.label}>
                  {t("forms.portalSubscription.labels.projectUrl")}
                </label>
                <input
                  type="url"
                  id="projectUrl"
                  name="projectUrl"
                  value={formData.projectUrl}
                  onChange={handleInputChange}
                  className={styles.input}
                  placeholder={t(
                    "forms.portalSubscription.placeholders.projectUrlExample",
                  )}
                  disabled={isSubmitting}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="projectImage" className={styles.label}>
                  {t("forms.portalSubscription.labels.projectImage")}
                </label>
                <div className={styles.fileUploadContainer}>
                  <label
                    className={`${styles.fileInputLabel} ${isSubmitting ? styles.disabled : ""}`}
                  >
                    <span className={styles.fileInputText}>
                      {projectImageFile || formData.projectImage
                        ? "Change Image"
                        : "Choose Image File"}
                    </span>
                    <input
                      type="file"
                      id="projectImage"
                      name="projectImage"
                      ref={projectImageInputRef}
                      accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                      onChange={handleProjectImageUpload}
                      className={styles.fileInput}
                      disabled={isSubmitting}
                    />
                  </label>
                  {(projectImagePreview || formData.projectImage) && (
                    <div className={styles.imagePreviewWrapper}>
                      <img
                        src={projectImagePreview || formData.projectImage}
                        alt="Project preview"
                        className={styles.imagePreview}
                      />
                      {(projectImageFile || formData.projectImage) && (
                        <button
                          type="button"
                          onClick={() => {
                            setFormData((p) => ({ ...p, projectImage: "" }));
                            setProjectImagePreview("");
                            setProjectImageFile(null);
                            if (projectImageInputRef.current) {
                              projectImageInputRef.current.value = "";
                            }
                          }}
                          className={styles.removeImageButton}
                          disabled={isSubmitting}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  )}
                  {!projectImagePreview && !projectImageFile && (
                    <p className={styles.helperText}>
                      JPEG, PNG, WebP, GIF (max 10MB)
                    </p>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.checkboxGroup}>
              <div className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  id="autoRenew"
                  name="autoRenew"
                  checked={formData.autoRenew}
                  onChange={handleInputChange}
                  className={styles.checkbox}
                  disabled={isSubmitting}
                />
                <label htmlFor="autoRenew" className={styles.checkboxLabel}>
                  {t("forms.portalSubscription.labels.autoRenew")}
                </label>
              </div>

              <div className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  id="scope"
                  name="scope"
                  checked={formData.scope}
                  onChange={handleInputChange}
                  className={styles.checkbox}
                  disabled={isSubmitting}
                />
                <label htmlFor="scope" className={styles.checkboxLabel}>
                  {t("forms.portalSubscription.labels.enableScope")}
                </label>
              </div>
            </div>

            <div className={styles.formActions}>
              <Button type="submit" variant="default" disabled={isSubmitting}>
                {isSubmitting ? "Updating..." : "Update Subscription Plan"}
              </Button>
            </div>
          </>
        )}
      </form>

      <FeedbackModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={modalTitle}
        message={modalMessage}
        type={modalType}
        confirmText={t("forms.portalSubscription.modals.close") || "Close"}
      />
    </>
  );
}
